// src/utils/loggers/deunaLogger.ts
// Utility helpers to log every step of the DeUna flow with consistent context

type DeunaLogLevel = "debug" | "info" | "warn" | "error";

export type DeunaLogContext = {
  source?: string;
  stage?: string;
  paymentId?: string;
  orderId?: string;
  sessionId?: string;
  userId?: string | number;
  checkoutSession?: string;
  cartItems?: number;
  attempt?: number;
};

export type DeunaLogEntry = {
  timestamp: string;
  level: DeunaLogLevel;
  message: string;
  context?: Partial<DeunaLogContext>;
  details?: unknown;
};

declare global {
  interface Window {
    __DEUNA_DEBUG_TIMELINE__?: DeunaLogEntry[];
  }
}

const consoleMap: Record<DeunaLogLevel, "log" | "info" | "warn" | "error"> = {
  debug: "log",
  info: "info",
  warn: "warn",
  error: "error",
};

const safeClone = (value: unknown): unknown => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (value === undefined) {
    return undefined;
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
};

const appendTimeline = (entry: DeunaLogEntry) => {
  if (typeof window === "undefined") {
    return;
  }

  if (!window.__DEUNA_DEBUG_TIMELINE__) {
    window.__DEUNA_DEBUG_TIMELINE__ = [];
  }

  window.__DEUNA_DEBUG_TIMELINE__!.push({
    ...entry,
    details: safeClone(entry.details),
    context: entry.context ? { ...entry.context } : undefined,
  });
};

const formatContextLabel = (context?: Partial<DeunaLogContext>) => {
  if (!context) {
    return "";
  }

  const pairs = Object.entries(context).filter(
    ([, value]) => value !== undefined && value !== null && value !== ""
  );

  if (!pairs.length) {
    return "";
  }

  const label = pairs
    .map(([key, value]) => `${key}:${String(value)}`)
    .join(" ");

  return ` ${label}`;
};

const logInternal = (
  level: DeunaLogLevel,
  baseContext: Partial<DeunaLogContext> | undefined,
  message: string,
  details?: unknown
) => {
  const timestamp = new Date().toISOString();
  const entry: DeunaLogEntry = {
    timestamp,
    level,
    message,
    context: baseContext,
    details,
  };
  const method = consoleMap[level] || "log";
  const consoleMethod =
    (console[method as keyof Console] as ((...args: unknown[]) => void) | undefined)?.bind(console) ??
    console.log.bind(console);
  const contextLabel = formatContextLabel(baseContext);

  if (details !== undefined) {
    consoleMethod(`[DEUNA][${level.toUpperCase()}][${timestamp}]${contextLabel} ${message}`, details);
  } else {
    consoleMethod(`[DEUNA][${level.toUpperCase()}][${timestamp}]${contextLabel} ${message}`);
  }

  appendTimeline(entry);
};

export const resetDeunaTimeline = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.__DEUNA_DEBUG_TIMELINE__ = [];
};

export const getDeunaTimeline = (): DeunaLogEntry[] => {
  if (typeof window === "undefined") {
    return [];
  }
  return window.__DEUNA_DEBUG_TIMELINE__ || [];
};

export const createDeunaLogger = (
  baseContext: Partial<DeunaLogContext> = {}
) => {
  const logWithLevel = (
    level: DeunaLogLevel,
    message: string,
    details?: unknown,
    extraContext?: Partial<DeunaLogContext>
  ) => {
    const mergedContext = {
      ...baseContext,
      ...extraContext,
      stage: extraContext?.stage || baseContext.stage,
    };
    logInternal(level, mergedContext, message, details);
  };

  return {
    debug: (
      message: string,
      details?: unknown,
      context?: Partial<DeunaLogContext>
    ) => logWithLevel("debug", message, details, context),
    info: (
      message: string,
      details?: unknown,
      context?: Partial<DeunaLogContext>
    ) => logWithLevel("info", message, details, context),
    warn: (
      message: string,
      details?: unknown,
      context?: Partial<DeunaLogContext>
    ) => logWithLevel("warn", message, details, context),
    error: (
      message: string,
      details?: unknown,
      context?: Partial<DeunaLogContext>
    ) => logWithLevel("error", message, details, context),
    timeline: (
      stage: string,
      details?: unknown,
      context?: Partial<DeunaLogContext>
    ) =>
      logWithLevel(
        "info",
        `Stage transition -> ${stage}`,
        details,
        context ? { ...context, stage } : { stage }
      ),
    extend: (context: Partial<DeunaLogContext>) =>
      createDeunaLogger({ ...baseContext, ...context }),
  };
};

export type DeunaLogger = ReturnType<typeof createDeunaLogger>;
